import React, { useState } from "react";
import api from "@/lib/api";
import { PageHeader, Card, Button, Textarea } from "@/components/UI";
import { Sparkle, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Bu hafta çiftlik performansını değerlendir ve iyileştirme önerileri ver.",
  "En çok kullanılan ilaçlar üzerinden stok yönetimi tavsiyesi ver.",
  "Süt üretimi trendini analiz et, düşüş varsa nedenlerini belirt.",
  "Gebelik oranımı arttırmak için ne yapmalıyım?",
  "Gider kalemlerini analiz et, tasarruf edilebilecek alanları öner.",
];

// Very small markdown-ish renderer (headings + bold + list) to avoid extra deps
function renderMarkdown(md) {
  if (!md) return null;
  const lines = md.split("\n");
  const out = [];
  let list = [];
  const flushList = () => {
    if (list.length) {
      out.push(<ul key={`ul-${out.length}`} className="list-disc pl-5 space-y-1 my-2">{list.map((l, i) => <li key={i}>{renderInline(l)}</li>)}</ul>);
      list = [];
    }
  };
  const renderInline = (s) => {
    const parts = s.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) => p.startsWith("**") && p.endsWith("**") ? <strong key={i}>{p.slice(2, -2)}</strong> : <span key={i}>{p}</span>);
  };
  lines.forEach((line, idx) => {
    if (/^###\s/.test(line)) { flushList(); out.push(<h4 key={idx} className="heading text-base mt-4 mb-1">{line.replace(/^###\s/, "")}</h4>); }
    else if (/^##\s/.test(line)) { flushList(); out.push(<h3 key={idx} className="heading text-lg mt-4 mb-2">{line.replace(/^##\s/, "")}</h3>); }
    else if (/^#\s/.test(line)) { flushList(); out.push(<h2 key={idx} className="heading text-xl mt-4 mb-2">{line.replace(/^#\s/, "")}</h2>); }
    else if (/^[-*]\s+/.test(line)) { list.push(line.replace(/^[-*]\s+/, "")); }
    else if (line.trim() === "") { flushList(); out.push(<div key={idx} className="h-2" />); }
    else { flushList(); out.push(<p key={idx} className="my-1">{renderInline(line)}</p>); }
  });
  flushList();
  return out;
}

export default function AIPage() {
  const [q, setQ] = useState("");
  const [answer, setAnswer] = useState("");
  const [loading, setLoading] = useState(false);
  const [wLoading, setWLoading] = useState(false);
  const [wr, setWr] = useState("");

  const ask = async (question) => {
    const finalQ = question || q;
    if (!finalQ.trim()) return;
    setLoading(true); setAnswer("");
    try {
      const { data } = await api.post("/ai/insight", { question: finalQ });
      setAnswer(data.answer);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hata");
    } finally { setLoading(false); }
  };

  const runWeekly = async () => {
    setWLoading(true); setWr("");
    try {
      const { data } = await api.get("/ai/weekly-report");
      setWr(data.report);
    } catch (e) { toast.error("Rapor hatası"); }
    finally { setWLoading(false); }
  };

  return (
    <div data-testid="ai-page">
      <PageHeader title="AI Danışman" subtitle="Çiftlik verilerinizi analiz eden yapay zeka asistanı." />

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <div className="flex items-center gap-2 mb-3">
              <Sparkle weight="fill" color="#C45B45" />
              <h3 className="heading text-lg">Soru Sor</h3>
            </div>
            <Textarea data-testid="ai-question" rows={3} value={q} onChange={e => setQ(e.target.value)} placeholder="Örn: Bu ayki gider yapısını analiz et..." />
            <div className="flex flex-wrap gap-2 mt-3">
              {SUGGESTIONS.map((s, i) => (
                <button key={s} data-testid={`ai-suggest-${i}`} onClick={() => { setQ(s); ask(s); }}
                  className="text-xs px-3 py-1.5 rounded-full border border-[#E5E2DC] hover:bg-[#F0EEE8]">{s}</button>
              ))}
            </div>
            <div className="mt-4">
              <Button data-testid="ai-submit" onClick={() => ask()} disabled={loading}>
                {loading ? "Analiz ediliyor..." : <><PaperPlaneTilt size={14} className="inline mr-1" /> Analiz Et</>}
              </Button>
            </div>
          </Card>

          {(loading || answer) && (
            <Card testId="ai-answer">
              <div className="text-[11px] uppercase tracking-widest text-[#6b746c] mb-2">Yanıt</div>
              {loading ? <div className="animate-pulse text-[#8a938c]">Yapay zeka çiftlik verilerinizi analiz ediyor...</div> : renderMarkdown(answer)}
            </Card>
          )}
        </div>

        <Card testId="weekly-report-card">
          <h3 className="heading text-lg mb-3">Haftalık AI Raporu</h3>
          <p className="text-sm text-[#5e675e] mb-4">Sürü, süt, veteriner ve finans verilerini bir arada yorumlar.</p>
          <Button variant="accent" data-testid="weekly-report-btn" onClick={runWeekly} disabled={wLoading}>
            {wLoading ? "Rapor oluşturuluyor..." : "Raporu Oluştur"}
          </Button>
          {wr && <div className="mt-4 border-t border-[#E5E2DC] pt-4 text-sm">{renderMarkdown(wr)}</div>}
        </Card>
      </div>
    </div>
  );
}
